import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Form, Input, Link, Popconfirm, Space, Table, Tooltip } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { InfoCircleIcon } from 'tdesign-icons-react';
import useTable from '@/hooks/useTable';
import { deleteRole, getRoles, updateRole } from '@/services/kafkaPermissionsService';
import ShadowCard from '@/components/ShadowCard';
import PermWrapper from '@/components/PermWrapper';
import Editable from '@/components/Editable';
import { renderDateTime } from '@/utils/common';
import DisableBuriedPermWrapper from '@/components/DisableWrapper/DisableBuriedPermWrapper';
import NoAuthAlert from '../../components/NoAuthAlert';
import Secret from '../../components/Secret';
import Modal from './Modal';

const { FormItem } = Form;

export default function Users() {
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const [visible, setVisible] = useState(false);
  const [update, setUpdate] = useState(false);

  // 查询初始化
  const initialData = useMemo(
    () => ({
      kafkaUser: searchParams.get('kafkaUser'),
    }),
    [searchParams],
  );

  const columns = [
    {
      colKey: 'kafkaUser',
      title: 'KafkaUser',
      width: 120,
    },
    {
      colKey: 'roleType',
      title: '认证方式',
      width: 150,
    },
    {
      colKey: 'roleKey',
      title: 'Password',
      cell: ({ row }) =>
        row.createByPlatform ? (
          <Secret>{row.roleKey}</Secret>
        ) : (
          <Tooltip content='非平台创建user因kafka安全机制无法获取密码信息，请联系您的集群管理员'>
            <Link hover='color' suffixIcon={<InfoCircleIcon color='var(--td-font-gray-2)' />}>
              *******
            </Link>
          </Tooltip>
        ),
    },
    {
      colKey: 'createByPlatform',
      title: '平台创建',
      cell: ({ row }) => (row.createByPlatform ? '是' : '否'),
      width: 90,
    },
    {
      colKey: 'createTime',
      title: '创建时间',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'updateTime',
      title: '更新时间',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'note',
      title: '备注',
      width: 240,
      cell: ({ row }) => <Editable text={row.note} onChange={(val) => onNoteChange(val, row)} />,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='kafka:auth:user:changepwd'>
            <Link theme='primary' onClick={() => doUpdate(row)} hover='color'>
              修改密码
            </Link>
          </PermWrapper>
          <Popconfirm theme='danger' content='确定删除？' onConfirm={() => handleDel(row)}>
            <DisableBuriedPermWrapper resource='kafka:auth:user:delete'>
              <Link theme='danger' hover='color'>
                移除
              </Link>
            </DisableBuriedPermWrapper>
          </Popconfirm>
        </Space>
      ),
      width: 140,
    },
  ];

  const onNoteChange = async (note, row) => {
    const params = {
      clusterName,
      kafkaUser: row.kafkaUser,
      type: row.roleType,
      note,
    };
    await updateRole(params);
    doRefresh();
  };

  // 修改
  const doUpdate = (row) => {
    setUpdate({
      clusterName,
      kafkaUser: row.kafkaUser,
      type: row.roleType,
    });
    setVisible(true);
  };

  // 删除
  const handleDel = async (row) => {
    await deleteRole({ clusterName, kafkaUser: row.kafkaUser });
    doRefresh();
  };

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getRoles({ ...params, clusterName });
    setData(data);
  });

  const { header, config, setData, doRefresh, doSearch, form } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <FormItem name='kafkaUser'>
        <Input placeholder='请输入KafkaUser' clearable />
      </FormItem>
    ),
    operators: (
      <>
        <PermWrapper resource='kafka:auth:user:create'>
          <Button
            theme='primary'
            onClick={() => {
              setVisible(true);
              setUpdate(false);
            }}
          >
            创建KafkaUser
          </Button>
        </PermWrapper>
      </>
    ),
  });

  const doInit = useMemoizedFn(async () => {
    // 有初始化条件
    if (searchParams.size > 1) {
      await doSearch(initialData);
      form.setFieldsValue(initialData);
    } else {
      doSearch();
    }
  });

  useEffect(() => {
    if (clusterName) {
      doInit();
    }
  }, [doInit, clusterName]);

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      // 刷新
      doRefresh();
    }
  };

  return (
    <Root>
      <NoAuthAlert name='KafkaUser' />
      <Body>
        {header}
        <Table {...config} />
        <Modal
          clusterName={clusterName}
          visible={visible}
          update={update}
          handleClose={handleClose}
        />
      </Body>
    </Root>
  );
}

const Root = styled.div`
  padding-right: 20px;
`;

const Body = styled(ShadowCard)`
  .t-form-inline .t-form__item {
    margin-right: var(--td-comp-margin-s);
  }
`;
